export const startOfDay = (date: Date): Date => {
  const start = new Date(date.getTime())
  start.setHours(0, 0, 0, 0)
  return start
}

export const startOfWeek = (date: Date): Date => {
  const start = new Date(date.getTime())
  const day = start.getDay()
  const diff = (day + 6) % 7
  start.setDate(start.getDate() - diff)
  start.setHours(0, 0, 0, 0)
  return start
}

export const startOfMonth = (date: Date): Date => {
  const start = new Date(date.getTime())
  start.setDate(1)
  start.setHours(0, 0, 0, 0)
  return start
}

export const startOfQuarter = (date: Date): Date => {
  const start = new Date(date.getTime())
  const quarterStartMonth = Math.floor(start.getMonth() / 3) * 3
  start.setMonth(quarterStartMonth, 1)
  start.setHours(0, 0, 0, 0)
  return start
}

export const startOfYear = (date: Date): Date => {
  const start = new Date(date.getTime())
  start.setMonth(0, 1)
  start.setHours(0, 0, 0, 0)
  return start
}

export const startOfShift = (date: Date): Date => {
  const start = new Date(date.getTime())
  const hour = start.getHours()
  if (hour < 6) {
    start.setDate(start.getDate() - 1)
    start.setHours(22, 0, 0, 0)
    return start
  }
  if (hour < 14) {
    start.setHours(6, 0, 0, 0)
    return start
  }
  if (hour < 22) {
    start.setHours(14, 0, 0, 0)
    return start
  }
  start.setHours(22, 0, 0, 0)
  return start
}

export const endOfDay = (start: Date): Date => {
  const end = new Date(start.getTime())
  end.setDate(end.getDate() + 1)
  end.setHours(0, 0, 0, 0)
  end.setMilliseconds(end.getMilliseconds() - 1)
  return end
}

export const endOfWeek = (start: Date): Date => {
  const end = new Date(start.getTime())
  end.setDate(end.getDate() + 7)
  end.setHours(0, 0, 0, 0)
  end.setMilliseconds(end.getMilliseconds() - 1)
  return end
}

export const endOfMonth = (start: Date): Date => {
  const end = new Date(start.getTime())
  end.setMonth(end.getMonth() + 1, 1)
  end.setHours(0, 0, 0, 0)
  end.setMilliseconds(end.getMilliseconds() - 1)
  return end
}

export const endOfQuarter = (start: Date): Date => {
  const end = new Date(start.getTime())
  end.setMonth(end.getMonth() + 3, 1)
  end.setHours(0, 0, 0, 0)
  end.setMilliseconds(end.getMilliseconds() - 1)
  return end
}

export const endOfYear = (start: Date): Date => {
  const end = new Date(start.getTime())
  end.setFullYear(end.getFullYear() + 1, 0, 1)
  end.setHours(0, 0, 0, 0)
  end.setMilliseconds(end.getMilliseconds() - 1)
  return end
}

export const endOfShift = (start: Date): Date => {
  const end = new Date(start.getTime())
  const hour = start.getHours()
  if (hour < 6) {
    end.setHours(6, 0, 0, 0)
    return end
  }
  if (hour < 14) {
    end.setHours(14, 0, 0, 0)
    return end
  }
  if (hour < 22) {
    end.setHours(22, 0, 0, 0)
    return end
  }
  end.setDate(end.getDate() + 1)
  end.setHours(6, 0, 0, 0)
  return end
}

/**
 * "Previous shift" (AB#6936): the last COMPLETED shift, e.g. 06:00–14:00 when it is
 * 15:30. Unlike the "so far" presets its end is a shift boundary, not now.
 */
export const PREVIOUS_SHIFT_RANGE_ID = 'previous-shift'

/** The completed shift before the one `now` falls in. */
export const getPreviousShiftRange = (now: Date): {start: Date; end: Date} => {
  const end = startOfShift(now)
  return {start: startOfShift(new Date(end.getTime() - 1)), end}
}

/**
 * Where a preset's window ends when nobody has navigated it: now, except for
 * "Previous shift", which is a completed shift and ends at the current shift's start.
 */
export const getPresetLiveEnd = (timeRangeId: string, now: Date): Date =>
  timeRangeId === PREVIOUS_SHIFT_RANGE_ID ? getPreviousShiftRange(now).end : now

export type ShiftKind = 'early' | 'late' | 'night'

/** Which of the three shifts (06–14, 14–22, 22–06) a shift START belongs to. */
export const getShiftKind = (shiftStart: Date): ShiftKind => {
  const hour = shiftStart.getHours()
  if (hour >= 6 && hour < 14) return 'early'
  if (hour >= 14 && hour < 22) return 'late'
  return 'night'
}

export type RollingRangeUnit = 'hours' | 'days'

export const ROLLING_RANGE_UNITS: RollingRangeUnit[] = ['hours', 'days']

export interface RollingRange {
  count: number
  unit: RollingRangeUnit
}

/** Upper bound of a free "last N" range, per unit (a year either way). */
export const MAX_ROLLING_RANGE_COUNT: Record<RollingRangeUnit, number> = {
  hours: 8784,
  days: 366,
}

const ROLLING_RANGE_PATTERN = /^last-(\d{1,4})-(hours|days)$/

/**
 * A free rolling range "last N hours / days" (AB#6936) is stored as its own preset
 * id, `last-<N>-<unit>`, so it is saved, restored, put in the URL and printed
 * exactly like the fixed presets. Null for any other id.
 */
export const parseRollingRange = (timeRangeId?: string | null): RollingRange | null => {
  const match = timeRangeId ? ROLLING_RANGE_PATTERN.exec(timeRangeId) : null
  if (!match) return null
  const count = Number(match[1])
  const unit = match[2] as RollingRangeUnit
  if (!Number.isInteger(count) || count < 1 || count > MAX_ROLLING_RANGE_COUNT[unit]) return null
  return {count, unit}
}

export const buildRollingRangeId = ({count, unit}: RollingRange): string => `last-${count}-${unit}`

/** Start of a rolling range ending at `end`; days step in calendar days (DST-safe). */
export const getRollingRangeStart = ({count, unit}: RollingRange, end: Date): Date => {
  const start = new Date(end.getTime())
  if (unit === 'days') {
    start.setDate(start.getDate() - count)
  } else {
    start.setTime(start.getTime() - count * 60 * 60 * 1000)
  }
  return start
}

export type SoFarRangeId =
  | 'day-so-far'
  | 'shift-so-far'
  | 'week-so-far'
  | 'month-so-far'
  | 'quarter-so-far'
  | 'year-so-far'

export const isSoFarRangeId = (value?: string | null): value is SoFarRangeId =>
  value === 'day-so-far' ||
  value === 'shift-so-far' ||
  value === 'week-so-far' ||
  value === 'month-so-far' ||
  value === 'quarter-so-far' ||
  value === 'year-so-far'

/**
 * What a time range's START means, which decides whether it may be moved.
 *
 * - `rolling`: "the last N hours/days/months" -- the start is just now minus N
 *   and means nothing on its own.
 * - `anchored`: "day/shift/week/.../year so far" -- the start is a boundary the
 *   user asked for.
 * - `custom`: picked dates (`'null'` is the custom window's id). A missing id
 *   counts as custom too: when in doubt, the start is not ours to move.
 */
export type TimeRangeKind = 'rolling' | 'anchored' | 'custom'

export const getTimeRangeKind = (timeRangeId?: string | null): TimeRangeKind => {
  if (!timeRangeId || timeRangeId === 'null') return 'custom'
  if (isSoFarRangeId(timeRangeId) || timeRangeId === PREVIOUS_SHIFT_RANGE_ID) return 'anchored'
  // Every other preset is computed as now minus a duration.
  return 'rolling'
}

export const getPreviousRangeForSoFar = (
  timeRangeId: SoFarRangeId,
  currentStart: Date,
): {start: Date; end: Date} => {
  const previousEnd = new Date(currentStart)
  const anchor = new Date(previousEnd.getTime() - 1)
  switch (timeRangeId) {
    case 'day-so-far':
      return {start: startOfDay(anchor), end: previousEnd}
    case 'shift-so-far':
      return {start: startOfShift(anchor), end: previousEnd}
    case 'week-so-far':
      return {start: startOfWeek(anchor), end: previousEnd}
    case 'month-so-far':
      return {start: startOfMonth(anchor), end: previousEnd}
    case 'quarter-so-far':
      return {start: startOfQuarter(anchor), end: previousEnd}
    case 'year-so-far':
      return {start: startOfYear(anchor), end: previousEnd}
    default:
      return {start: startOfDay(anchor), end: previousEnd}
  }
}

export const getFullPeriodEndForSoFar = (timeRangeId: SoFarRangeId, currentStart: Date): Date => {
  switch (timeRangeId) {
    case 'day-so-far':
      return endOfDay(currentStart)
    case 'shift-so-far':
      return endOfShift(currentStart)
    case 'week-so-far':
      return endOfWeek(currentStart)
    case 'month-so-far':
      return endOfMonth(currentStart)
    case 'quarter-so-far':
      return endOfQuarter(currentStart)
    case 'year-so-far':
      return endOfYear(currentStart)
    default:
      return endOfDay(currentStart)
  }
}

/**
 * The moment a widget's relative override ("Shift so far", "Last 8 hours", ...)
 * counts back from (AB#6968).
 *
 * A dashboard preset that ends before now ("Previous shift") must not drag the
 * widget's window with it: "Shift so far" on a "Previous shift" dashboard is the
 * current shift, not an empty window. So the reference is now, moved back only by
 * however far the user navigated the dashboard away from its live window; on a
 * navigated dashboard the widget still shows its part of that historical period.
 * For presets ending at now this is exactly the dashboard's end. A custom window
 * has no live position, so its end is the reference.
 */
export const getOverrideReferenceTime = (
  dashboardTimeRangeId: string | null | undefined,
  dashboardEnd: Date,
  now: Date,
): Date => {
  if (!dashboardTimeRangeId || getTimeRangeKind(dashboardTimeRangeId) === 'custom') {
    return dashboardEnd
  }
  const navigatedBack = Math.max(
    0,
    getPresetLiveEnd(dashboardTimeRangeId, now).getTime() - dashboardEnd.getTime(),
  )
  return new Date(now.getTime() - navigatedBack)
}
